import {
  requiresTrialActivationConfirm,
  shouldConfirmTrialActivation,
} from '@repo/hooks-trial-activation-confirm'
import { isRequestForTeamWorkspace } from '@repo/studio-hooks-auth/workspace-request-headers'
import { useQueryClient } from '@tanstack/react-query'
import { useUserQuery } from 'auth/hooks'
import { COUNTRY_COOKIE_NAME } from 'constants/cookies'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { getCookie } from 'utils/dom'
import { BALANCE_QUERY_VARIABLES, getBalanceQueryOptions } from './coin-balance-query'
import { requestTrialActivationConfirm } from './use-trial-activation-confirm-dialog'

export function useConfirmTrialActivation() {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { data: isStudioAiTrial } = useUserQuery(undefined, {
    select: data => data.me?.user?.subscriptionAccess.studioAi.subscription?.isTrial === true,
  })

  return React.useCallback(
    async (input: { coinPrice?: number; willActivateTrial?: boolean }) => {
      if (isRequestForTeamWorkspace()) {
        return true
      }
      const locale = i18n.resolvedLanguage ?? i18n.language
      const cookie = getCookie(COUNTRY_COOKIE_NAME)
      const country = cookie.length > 0 ? cookie : undefined
      if (isStudioAiTrial !== true || !requiresTrialActivationConfirm({ locale, country })) {
        return true
      }
      if (input.willActivateTrial) {
        return await requestTrialActivationConfirm()
      }
      if (input.coinPrice === undefined) {
        return true
      }
      let balance: number
      try {
        const wallet = await queryClient.fetchQuery(
          getBalanceQueryOptions(BALANCE_QUERY_VARIABLES, null),
        )
        balance = Number(wallet.balance)
      } catch {
        // Unknown remaining coins: ask before a generate that might skip the trial.
        return await requestTrialActivationConfirm()
      }
      if (
        !shouldConfirmTrialActivation({
          locale,
          country,
          isOnTrial: true,
          balance,
          coinPrice: input.coinPrice,
        })
      ) {
        return true
      }
      return await requestTrialActivationConfirm()
    },
    [i18n.language, i18n.resolvedLanguage, isStudioAiTrial, queryClient],
  )
}
